import { create } from "zustand";

export type SideCartTrigger =
  "nav" | "auto_after_add" | "quick_add" | "referral_toast";

interface LayoutState {
  heroInView: boolean;
  setHeroInView: (inView: boolean) => void;
  bannerHeight: number;
  setBannerHeight: (height: number) => void;
  headerHeight: number;
  setHeaderHeight: (height: number) => void;
  isHeaderHidden: boolean;
  setIsHeaderHidden: (hidden: boolean) => void;
  isMenuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  toggleMenu: () => void;
  activeDropdown: "shop" | "resources" | null;
  setActiveDropdown: (dropdown: "shop" | "resources" | null) => void;
  pendingAccessoriesReopen: boolean;
  isSideCartOpen: boolean;
  /** Why the cart opened. Without it a deliberate nav click and the automatic
   *  open after add-to-cart are indistinguishable in analytics. */
  sideCartTrigger: SideCartTrigger | null;
  openSideCart: (trigger?: SideCartTrigger) => void;
  closeSideCart: () => void;
  isPricingNoteModalOpen: boolean;
  openPricingNoteModal: () => void;
  closePricingNoteModal: () => void;
  isSweepstakesOpen: boolean;
  setSweepstakesOpen: (open: boolean) => void;
  closeOverlays: () => void;
}

const useLayoutStore = create<LayoutState>((set) => ({
  heroInView: true,
  setHeroInView: (inView) => set({ heroInView: inView }),
  bannerHeight: 0,
  setBannerHeight: (height) => set({ bannerHeight: height }),
  headerHeight: 56,
  setHeaderHeight: (height) => set({ headerHeight: height }),
  isHeaderHidden: false,
  setIsHeaderHidden: (hidden) => set({ isHeaderHidden: hidden }),
  isMenuOpen: false,
  openMenu: () =>
    set({
      activeDropdown: null,
      isMenuOpen: true,
      isSideCartOpen: false,
    }),
  closeMenu: () => set({ isMenuOpen: false }),
  toggleMenu: () =>
    set((state) => ({
      activeDropdown: null,
      isMenuOpen: !state.isMenuOpen,
      isSideCartOpen: false,
    })),
  activeDropdown: null,
  setActiveDropdown: (dropdown) => set({ activeDropdown: dropdown }),
  pendingAccessoriesReopen: false,
  isSideCartOpen: false,
  sideCartTrigger: null,
  openSideCart: (trigger = "nav") =>
    set({
      activeDropdown: null,
      isMenuOpen: false,
      isSideCartOpen: true,
      sideCartTrigger: trigger,
    }),
  closeSideCart: () => set({ isSideCartOpen: false }),
  isPricingNoteModalOpen: false,
  openPricingNoteModal: () => set({ isPricingNoteModalOpen: true }),
  closePricingNoteModal: () => set({ isPricingNoteModalOpen: false }),
  isSweepstakesOpen: false,
  setSweepstakesOpen: (open) => set({ isSweepstakesOpen: open }),
  closeOverlays: () =>
    set({
      activeDropdown: null,
      isMenuOpen: false,
      isSideCartOpen: false,
      isPricingNoteModalOpen: false,
      pendingAccessoriesReopen: false,
    }),
}));

export default useLayoutStore;
